// 게시판 html에서 url가로채서 연결시키기
import { onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

export function useSimpleLinkIntercept(containerRef, opts = {}) {
  const router = useRouter()

  const {
    devMode = false,
    internalHosts = devMode
      ? ['d25web.thegam.io', window.location.host,]
      : ['nangmancalendar.com', 'www.nangmancalendar.com', window.location.host],
    allowWildcard = true,
    debug = false,
    sendMessageToApp = ({ action, url, route, message }) => {
      if (window?.ReactNativeWebView?.postMessage) {
        const messageData = { action, payload: { url, route, message } }
        window.ReactNativeWebView.postMessage(JSON.stringify(messageData))
      }
    },
  } = opts

  const TAG = '[useSimpleLinkIntercept]'
  const log = (...a) => debug && console.log(TAG, ...a)

  const hasScheme = (href) => /^[a-zA-Z][\w+.-]*:/.test(href || '')
  const isHttp = (href) => /^https?:\/\//i.test(href || '')

  const isInternalHost = (host) => {
    if (!host) return true
    return internalHosts.some((allowed) =>
      allowWildcard 
        ? host === allowed || host.endsWith('.' + allowed.replace(/^www\./, '')) || host === allowed
        : host === allowed
    )
  }

  const classify = (href) => {
    if (!href) return 'skip'
    if (href.startsWith('#')) return 'internal'
    if (href.startsWith('/')) return 'internal'
    if (!hasScheme(href)) return 'internal'
    if (!isHttp(href)) return 'pass'
    try {
      const u = new URL(href)
      return isInternalHost(u.host) ? 'internal' : 'external'
    } catch {
      return 'pass'
    }
  }

  const IN_PLACE_PATH_RE = /^\/(guide|customer)(\/|$)/i

  /** 내부에서 그대로 열어야 하는지(= location.assign) 결정 */
  const shouldOpenInPlace = (href) => {
    if (!href) return false
    const s = href.trim()
    if (s.startsWith('#')) return true

    try {
      const u = new URL(s, window.location.href)
      const p = u.pathname || '/'
      return IN_PLACE_PATH_RE.test(p)
    } catch {
      return true
    }
  }

  const openInternal = (href) => {
    log('openInternal', href)

    try {
      const u = new URL(href, window.location.href)
      if (u.origin === window.location.origin && router) {
        router.push(u.pathname + u.search + u.hash)
        return
      }
    } catch { /* fallthrough */ }

    window.location.assign(href)
  }

  const openExternal = (url) => {
    log('openExternal', url, !!window.ReactNativeWebView ? '(RN)' : '(web)')

    if (window?.ReactNativeWebView) {
      sendMessageToApp({ action: 'openExternal', url });
    } else {
      window.open(url, '_blank', 'noopener')
    }
  }

  const onClick = (e) => {
    ({action: 'debug', message: `onClick`})
    const root = containerRef?.value
    if (!root) return
    
    const a = e.target instanceof Element ? e.target.closest('a') : null
    if (!a || !root.contains(a)) return

    const href = a.getAttribute('href')
    const kind = classify(href)

    log('anchor click', { href, kind })

    if (kind === 'pass' || kind === 'skip') return
    if (e.metaKey || e.ctrlKey || e.button === 1) return

    e.preventDefault()
    e.stopPropagation()

    if (kind === 'internal') openInternal(href)
    else openExternal(href)
  }
  
  watch(() => containerRef?.value, (newRef, oldRef) => {
    if (oldRef && oldRef !== newRef) oldRef.removeEventListener('click', onClick, true)
    if (newRef && oldRef !== newRef) {
      newRef.addEventListener('click', onClick, true)  
    }
  })

  onMounted(() => {
    log('mounted', { devMode, internalHosts })

    containerRef.value?.addEventListener('click', onClick, true)
  })
  onBeforeUnmount(() => {
    containerRef.value?.removeEventListener('click', onClick, true)
  })
}
